import React, { useEffect, useState } from 'react';
import './index.css'; // グローバルCSS (Tailwind含む)
import Header from './components/layout/Header'; // Header をインポート
import Footer from './components/layout/Footer'; // Footer をインポート
import TopArea from './components/layout/TopArea'; // TopArea をインポート
import UnderArea from './components/layout/UnderArea'; // UnderArea をインポート

// GameWrapper とその依存コンポーネントを page.tsx から移植
import { useGameController } from './components/game/GameController';
import TypingArea from './components/game/TypingArea';
import ResultScreen from './components/game/ResultScreen';
import OverallPerformanceSummary from './components/game/OverallPerformanceSummary';

const CLEARED_STAGES_STORAGE_KEY = 'clearedStageIds';

const GameWrapper: React.FC = () => {
  const {
    currentStage,
    currentLine,
    currentLineIndex,
    userInput,
    gameState,
    mistakes,
    stageTimeTaken,
    score,
    showHint,
    completedStageLines,
    stages,
    clearedStageIds,
    actions,
    gameSessionStartTime,
    gameSessionEndTime,
    gameSession, // gameSession を追加
  } = useGameController({
    onStageClear: (stageId, mistakes, timeTaken, score) => {
      console.log(
        `Stage ${stageId} cleared! Mistakes: ${mistakes}, Time: ${timeTaken}s, Score: ${score}`,
      );
    },
    onGameOver: () => {
      console.log('Game Over! All stages cleared!');
    },
  });

  const [hasCompletedHistory, setHasCompletedHistory] = useState(false);
  const [hasPartialHistory, setHasPartialHistory] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [showOverallSummary, setShowOverallSummary] = useState(false);
  const [currentGameTimeFormatted, setCurrentGameTimeFormatted] = useState('0秒');

  useEffect(() => {
    const storedClearedStages = localStorage.getItem(CLEARED_STAGES_STORAGE_KEY);
    if (storedClearedStages) {
      const loadedClearedStageIds: string[] = JSON.parse(storedClearedStages);
      if (
        loadedClearedStageIds.length > 0 &&
        stages &&
        loadedClearedStageIds.length === stages.length
      ) {
        setHasCompletedHistory(true);
        setHasPartialHistory(false);
      } else if (loadedClearedStageIds.length > 0) {
        setHasPartialHistory(true);
        setHasCompletedHistory(false);
      }
    } else {
      setHasPartialHistory(false);
      setHasCompletedHistory(false);
    }
    setLoadingInitialData(false);
  }, [stages, clearedStageIds]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (gameState === 'playing' && gameSessionStartTime && !gameSessionEndTime) {
      timer = setInterval(() => {
        const durationInSeconds = Math.round((Date.now() - gameSessionStartTime) / 1000);
        const hours = Math.floor(durationInSeconds / 3600);
        const minutes = Math.floor((durationInSeconds % 3600) / 60);
        const seconds = durationInSeconds % 60;
        setCurrentGameTimeFormatted(
          (hours > 0 ? hours + '時間' : '') +
            (minutes > 0 || hours > 0 ? minutes + '分' : '') +
            seconds +
            '秒',
        );
      }, 1000);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [gameState, gameSessionStartTime, gameSessionEndTime]);

  const handleShowOverallPerformance = () => {
    setShowOverallSummary(true);
  };

  // OverallPerformanceSummary からTOPへ戻るための新しいコールバック
  const handleGoToTitleFromSummary = () => {
    actions.goToTitle(); // ゲームの状態を初期化
    setShowOverallSummary(false); // サマリー画面を非表示にする
  };

  if (loadingInitialData) {
    return <p className="text-center text-gray-500 py-10">データを読み込み中...</p>;
  }

  if (showOverallSummary) {
    // dummyDevelopmentReport の定義を削除
    // const dummyDevelopmentReport = {
    //   skillAcquisition: { 順次処理: 80, 分岐処理: 75, 繰り返し: 90 },
    //   typingAnalysis: {
    //     averageKPM: 250,
    //     accuracy: 95,
    //     weakKeys: ['Q', 'P'],
    //   },
    // };

    // gameSession から KPM と accuracy を取得して typingScore を計算
    const averageKPM = gameSession?.totalKPM ?? 0;
    const accuracy = gameSession?.totalAccuracy ?? 0;

    const typingScore = Math.round((averageKPM * accuracy) / 100);

    const getEvaluationComment = (score: number): string => {
      // 最大スコアをKPMの一般的な上限（例：400KPM）と精度100%と仮定して調整
      // typingScore = (KPM * Accuracy) / 100
      // 例: KPM 400, Accuracy 100 => typingScore = (400 * 100) / 100 = 400
      // この最大値400を基準に10段階評価を設定
      if (score >= 360) return '素晴らしい！完璧なパフォーマンスです！'; // 400 の 90%
      if (score >= 320) return '非常に優秀です！あと一歩で完璧！'; // 400 の 80%
      if (score >= 280) return '優秀です！素晴らしい結果です！'; // 400 の 70%
      if (score >= 240) return '良い成績です！この調子で頑張りましょう！'; // 400 の 60%
      if (score >= 200) return 'まずまずの成績です。もう少し練習しましょう！'; // 400 の 50%
      if (score >= 160) return '平均的な成績です。基本をしっかり押さえましょう。'; // 400 の 40%
      if (score >= 120) return 'もう少し努力が必要です。諦めずに挑戦しましょう！'; // 400 の 30%
      if (score >= 80) return '改善の余地ありです。基礎から復習しましょう。'; // 400 の 20%
      if (score >= 40) return 'まだ始まったばかりです！焦らずゆっくり進めましょう。'; // 400 の 10%
      return '頑張りましょう！これからです！'; // 400 の 10%未満
    };
    const overallEvaluationComment = getEvaluationComment(typingScore);

    let totalPlayTimeFormatted = '計算中...';
    if (gameSessionStartTime && gameSessionEndTime) {
      const durationInSeconds = Math.round((gameSessionEndTime - gameSessionStartTime) / 1000);
      const hours = Math.floor(durationInSeconds / 3600);
      const minutes = Math.floor((durationInSeconds % 3600) / 60);
      const seconds = durationInSeconds % 60;
      totalPlayTimeFormatted =
        (hours > 0 ? hours + '時間' : '') +
        (minutes > 0 || hours > 0 ? minutes + '分' : '') +
        seconds +
        '秒';
    } else if (gameSessionStartTime && !gameSessionEndTime) {
      totalPlayTimeFormatted = 'プレイ中...';
    }

    return (
      <OverallPerformanceSummary
        typingScore={typingScore}
        overallEvaluationComment={overallEvaluationComment} // 更新されたコメントを使用
        totalPlayTime={totalPlayTimeFormatted}
        onGoToTitle={handleGoToTitleFromSummary}
      />
    );
  }

  if (gameState === 'initial') {
    return (
      <div className="text-center py-10 space-y-4">
        {hasCompletedHistory ? (
          <>
            <h3 className="text-xl font-semibold text-green-700">全ステージクリア済みです！</h3>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleShowOverallPerformance}
                className="max-w-xs px-8 py-4 bg-blue-500 text-white font-bold rounded-lg shadow-lg hover:bg-blue-600 transition-transform transform hover:scale-105 duration-150 text-xl h-24 flex items-center justify-center"
              >
                結果を見る
              </button>
              <button
                onClick={() => {
                  actions.resetProgress();
                  actions.initializeGame();
                }}
                className="max-w-xs px-8 py-4 bg-red-500 text-white font-bold rounded-lg shadow-lg hover:bg-red-600 transition-transform transform hover:scale-105 duration-150 text-xl h-24 flex items-center justify-center"
              >
                最初から開始する (進行状況リセット)
              </button>
            </div>
          </>
        ) : hasPartialHistory ? (
          <>
            <h3 className="text-xl font-semibold text-blue-700">前回の続きがあります。</h3>
            <div className="flex justify-center space-x-4">
              <button
                onClick={actions.initializeGame}
                className="max-w-xs px-8 py-4 bg-green-500 text-white font-bold rounded-lg shadow-lg hover:bg-green-600 transition-transform transform hover:scale-105 duration-150 text-xl h-24 flex items-center justify-center"
              >
                続きから開始する
              </button>
              <button
                onClick={() => {
                  actions.resetProgress();
                  actions.initializeGame();
                }}
                className="max-w-xs px-8 py-4 bg-red-500 text-white font-bold rounded-lg shadow-lg hover:bg-red-600 transition-transform transform hover:scale-105 duration-150 text-xl h-24 flex items-center justify-center"
              >
                最初から開始する
                <br />
                (進行状況リセット)
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={actions.initializeGame}
            className="w-full max-w-xs px-8 py-4 bg-blue-500 text-white font-bold rounded-lg shadow-lg hover:bg-blue-600 transition-transform transform hover:scale-105 duration-150 text-xl h-24 flex items-center justify-center"
          >
            ゲームスタート！
          </button>
        )}
      </div>
    );
  }

  if (gameState === 'loading') {
    return <p className="text-center text-gray-500 py-10">ステージを読み込み中...</p>;
  }

  if (gameState === 'all_cleared') {
    return (
      <div className="text-center py-10">
        <h2 className="text-3xl font-bold text-green-600 mb-4">
          全ステージクリア！おめでとうございます！
        </h2>
        <p className="text-lg text-gray-700 mb-6">
          あなたは全てのプログラミングチャレンジを達成しました。
        </p>
        <div className="space-y-4">
          <button
            onClick={handleShowOverallPerformance}
            className="w-full max-w-xs px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition-colors duration-150 text-lg mb-2"
          >
            総合結果を見る
          </button>
          <button
            onClick={() => {
              actions.resetProgress();
              actions.initializeGame();
            }}
            className="w-full max-w-xs px-6 py-3 bg-yellow-500 text-white font-semibold rounded-lg shadow-md hover:bg-yellow-600 transition-colors duration-150 text-lg"
          >
            最初からもう一度プレイする
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'result' && currentStage) {
    return (
      <ResultScreen
        stageName={currentStage.name}
        mistakes={mistakes}
        timeTaken={stageTimeTaken ?? undefined}
        score={score}
        storyAfter={currentStage.storyAfter}
        programmingConcept={currentStage.programmingConcept}
        onNextStage={() => actions.goToNextStage()} // kpm と acc の引数を削除
        onRetryStage={actions.retryCurrentStage}
        finalCode={completedStageLines.join('\n')}
        onShowOverallPerformance={handleShowOverallPerformance}
      />
    );
  }

  if (!currentStage) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 mb-4">エラーが発生しました。ゲームを初期化してください。</p>
        <button
          onClick={actions.initializeGame}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition-colors duration-150 text-lg"
        >
          ゲームを初期化
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {completedStageLines.length > 0 && (
        <div className="mb-4 p-4 bg-gray-100 rounded-md shadow min-h-[150px] overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">完成したコード:</h3>
          <pre className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-200 p-3 rounded-md">
            {completedStageLines.join('\n')}
          </pre>
        </div>
      )}

      <div className="text-center p-4 bg-blue-50 rounded-lg shadow">
        <h2 className="text-2xl font-semibold text-blue-700">{currentStage.name}</h2>
        {currentStage.storyBefore && gameState === 'ready' && (
          <p className="italic text-sm text-gray-600 mt-2 p-2 bg-blue-100 rounded">
            {currentStage.storyBefore}
          </p>
        )}
        {gameState === 'playing' || gameState === 'cleared' || gameState === 'result' ? (
          <>
            <p className="text-md text-blue-600 mt-1">
              現在の行: {currentLineIndex + 1} / {currentStage.lines.length}
            </p>
            <p className="text-md text-red-500">ミス: {mistakes}</p>
            <p className="text-md text-purple-500">経過時間: {currentGameTimeFormatted}</p>
            {gameState === 'playing' && currentLine?.explanation && (
              <div className="mt-2">
                <button
                  onClick={actions.requestHint}
                  className="px-3 py-1 bg-yellow-400 text-yellow-800 rounded-md hover:bg-yellow-500 transition-colors text-xs shadow"
                >
                  ヒントを見る
                </button>
                {showHint && (
                  <p className="text-sm text-gray-500 mt-1 bg-yellow-50 p-1 rounded">
                    ヒント: {currentLine.explanation}
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-md text-gray-500 mt-1">「ゲーム開始」ボタンを押してスタート！</p>
        )}
      </div>

      <TypingArea
        currentLineText={currentLine?.code || ''}
        userInput={userInput}
        onInputChange={actions.handleInputChange}
        isReadOnly={gameState !== 'playing'}
      />

      <div className="flex flex-col items-center justify-center space-y-4 mt-8">
        {gameState === 'ready' && (
          <button
            onClick={actions.startGame}
            className="w-full max-w-xs px-8 py-3 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600 transition-transform transform hover:scale-105 duration-150 text-lg"
          >
            ゲーム開始
          </button>
        )}
        {gameState === 'playing' && (
          <p className="text-lg text-center text-gray-700 animate-pulse">
            タイピング中... 頑張って！
          </p>
        )}
      </div>
    </div>
  );
};

// page.tsx の Home コンポーネントのUI構造を App に適用
function App() {
  // body のクラスを適用 (元の layout.tsx の geistSans.variable などは省略)
  useEffect(() => {
    document.body.className = 'antialiased flex flex-col min-h-screen';
    // 必要に応じて、ここで document.title を設定
    document.title = 'プログラミングxタイピング';
    return () => {
      document.body.className = ''; // クリーンアップ
    };
  }, []);

  return (
    <>
      {' '}
      {/* React.Fragment を使用 */}
      <Header />
      <TopArea />
      {/* page.tsx の <main> に相当する部分 */}
      <main className="flex-grow container mx-auto p-4">
        {/* page.tsx の Home コンポーネントの内容を展開 */}
        <div className="w-full max-w-3xl mx-auto bg-white shadow-2xl rounded-xl p-6 sm:p-10">
          <header className="mb-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-400 mb-2">
              プログラミング x タイピング
            </h1>
            <p className="text-lg text-gray-600">
              プログラムコードの書き方をタイピングしながら学んでいこう！
            </p>
          </header>
          <GameWrapper />
        </div>
      </main>
      <UnderArea />
      <Footer />
    </>
  );
}

export default App;
